import { useState } from "react";
import { ChevronRight, Lightbulb } from "lucide-react";
import { type ToolCallInfo } from "@/lib/api/chat";

interface ThinkingCardProps {
  tool: ToolCallInfo;
  isStreaming?: boolean;
  // Optional controlled mode props for mutual exclusion
  isExpanded?: boolean;
  onToggle?: (toolId: string) => void;
}

/**
 * ThinkingCard - A collapsible card component for displaying agent thinking/reasoning
 *
 * Features:
 * - Shows title with shimmer effect when running
 * - Expands to show full thought content
 * - Animated chevron rotation
 * - Supports both controlled (mutual exclusion) and uncontrolled modes
 */
export function ThinkingCard({
  tool,
  isStreaming: _isStreaming = false,
  isExpanded: controlledExpanded,
  onToggle,
}: ThinkingCardProps) {
  // Use internal state only if not in controlled mode
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled =
    controlledExpanded !== undefined && onToggle !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  // Extract thinking data from tool input (may be stored in different places)
  // Different reasoning tools use different field names:
  // - think tool: uses 'thought' or 'reasoning'
  // - analyze tool: uses 'analysis'
  const thinkingData = (tool as any).input || {};
  const thinkingTitle = thinkingData.title || "思考中...";
  const thought =
    thinkingData.thought ||
    thinkingData.reasoning ||
    thinkingData.analysis ||
    "";
  const action = thinkingData.action || thinkingData.next_action;

  const isRunning = tool.status === "running";

  // Show actual title when running or expanded, "思考内容" when collapsed and completed
  const displayTitle = isRunning || isExpanded ? thinkingTitle : "思考内容";

  const handleToggle = () => {
    if (isControlled) {
      // In controlled mode, notify parent
      onToggle!(tool.id);
    } else {
      // In uncontrolled mode, manage state internally
      setInternalExpanded(!internalExpanded);
    }
  };

  return (
    <div
      className={`rounded-lg border transition-colors ${
        isRunning
          ? "border-amber-200 bg-amber-50/50"
          : "border-slate-200 bg-slate-50/50"
      }`}
    >
      <button
        onClick={handleToggle}
        className="w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-slate-100/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Lightbulb
            className={`w-4 h-4 flex-shrink-0 ${
              isRunning ? "text-amber-500" : "text-slate-500"
            }`}
          />
          <div className="relative flex-1 min-w-0 overflow-hidden">
            <span
              className={`text-sm font-medium truncate block ${
                isExpanded ? "whitespace-normal" : "line-clamp-1"
              }`}
            >
              {displayTitle}
            </span>
            {/* Shimmer overlay when running */}
            {isRunning && (
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="shimmer-effect absolute inset-0 w-[200px] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {tool.executionTime && (
            <span className="text-xs text-emerald-600">
              {tool.executionTime.toFixed(2)}s
            </span>
          )}
          <ChevronRight
            className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
              isExpanded ? "rotate-90" : ""
            }`}
          />
        </div>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-200/50">
          {thought && (
            <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
              {thought}
            </p>
          )}

          {action && action !== "continue" && (
            <div className="mt-2 pt-2 border-t border-slate-200/30">
              <span className="text-xs text-slate-400 mr-1">下一步:</span>
              <span className="text-xs text-slate-600 font-medium">
                {action}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Check if a tool is a reasoning/thinking tool
 * Uses the isReasoning flag set by the backend
 * Backend must set is_reasoning or x_agno_is_reasoning flags
 */
export function isThinkingTool(isReasoning?: boolean): boolean {
  return isReasoning === true;
}
